/**
 * Which Firebase project this computer talks to. The values are public identifiers (the same ones
 * any web page of the project exposes), not secrets: access is decided by each person's sign-in
 * and the server-side rules. They can be built into the installer (.env) or entered on a computer
 * after installation, which is how additional terminals join the restaurant's cloud.
 */
export type CloudConfig = { apiKey: string; authDomain: string; projectId: string; storageBucket: string; messagingSenderId: string; appId: string; region: string;
  /** Address of the trusted backend when it runs as a Cloudflare Worker. Empty = Firebase Cloud Functions in `region`. */
  apiUrl: string;
};

const KEY = "mm.cloud";
const CODE_PREFIX = "MM-CLOUD:";
const FIELDS = ["apiKey", "authDomain", "projectId", "storageBucket", "messagingSenderId", "appId"] as const;

function validate(candidate: Partial<Record<keyof CloudConfig, unknown>>): CloudConfig | string {
  const text = (key: keyof CloudConfig) => typeof candidate[key] === "string" ? (candidate[key] as string).trim() : "";
  const config: CloudConfig = { apiKey: text("apiKey"), authDomain: text("authDomain"), projectId: text("projectId"), storageBucket: text("storageBucket"), messagingSenderId: text("messagingSenderId"), appId: text("appId"), region: text("region") || "asia-south1", apiUrl: text("apiUrl").replace(/\/+$/, "") };
  if (config.apiUrl && !/^https:\/\/[a-z0-9.-]+(\/[\w./-]*)?$/i.test(config.apiUrl)) return "The server address must start with https://";
  if (!/^AIza[\w-]{20,}$/.test(config.apiKey)) return "The apiKey is missing or not a Firebase web key.";
  if (!/^[a-z0-9-]{4,40}$/.test(config.projectId)) return "The projectId is missing.";
  if (!/^1:\d+:web:[0-9a-f]+$/.test(config.appId)) return "The appId is missing.";
  if (!/^[a-z0-9-]+$/.test(config.region)) return "The region is not valid.";
  config.authDomain ||= `${config.projectId}.firebaseapp.com`;
  return config;
}

/** Accepts a connection code, the JSON object, or the `const firebaseConfig = { … }` snippet copied from the Firebase console. */
export function parseCloudConfig(input: string): CloudConfig | string {
  const text = input.trim();
  if (!text) return "Paste the connection code or the Firebase configuration.";
  try {
    if (text.startsWith(CODE_PREFIX)) return validate(JSON.parse(atob(text.slice(CODE_PREFIX.length).replace(/\s+/g, ""))) as Partial<CloudConfig>);
    if (text.startsWith("{")) { try { return validate(JSON.parse(text) as Partial<CloudConfig>); } catch { /* fall through: a JavaScript object literal, not JSON */ } }
  } catch { return "That connection code is damaged. Copy it again."; }
  const found: Record<string, string> = {};
  for (const match of text.matchAll(/\b(apiKey|authDomain|projectId|storageBucket|messagingSenderId|appId|region|apiUrl)\b["']?\s*:\s*["']([^"']+)["']/g)) found[match[1]!] = match[2]!;
  return Object.keys(found).length === 0 ? "No Firebase configuration was found in that text." : validate(found);
}

export const connectionCode = (config: CloudConfig) => `${CODE_PREFIX}${btoa(JSON.stringify(config))}`;

function builtIn(): CloudConfig | null {
  const env = import.meta.env;
  const config = validate({
    apiKey: env.VITE_FIREBASE_API_KEY, authDomain: env.VITE_FIREBASE_AUTH_DOMAIN, projectId: env.VITE_FIREBASE_PROJECT_ID, storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID, appId: env.VITE_FIREBASE_APP_ID, region: env.VITE_FIREBASE_FUNCTIONS_REGION, apiUrl: env.VITE_API_URL
  });
  return typeof config === "string" ? null : config;
}
function entered(): CloudConfig | null {
  try {
    const config = validate(JSON.parse(localStorage.getItem(KEY) ?? "null") as Partial<CloudConfig> ?? {});
    return typeof config === "string" ? null : config;
  } catch { return null; }
}

const OFF = "mm.cloud.off";
const switchedOff = () => { try { return localStorage.getItem(OFF) === "1"; } catch { return false; } };
/** A configuration entered on this computer wins over the one built into the installer, which can also be switched off here. */
export const activeCloudConfig = (): CloudConfig | null => entered() ?? (switchedOff() ? null : builtIn());
export const cloudConfigSource = (): "entered" | "built-in" | "none" => entered() ? "entered" : !switchedOff() && builtIn() ? "built-in" : "none";
/** The project this build would use if the computer had not been switched to local mode. */
export const builtInCloudProject = (): string | null => builtIn()?.projectId ?? null;
export function saveCloudConfig(config: CloudConfig) { localStorage.setItem(KEY, JSON.stringify(config)); localStorage.removeItem(OFF); }
/** Returns this computer to local mode. Nothing in the cloud and nothing saved locally is deleted. */
export function clearCloudConfig() { localStorage.removeItem(KEY); if (builtIn()) localStorage.setItem(OFF, "1"); }
export const reconnectBuiltInCloud = () => localStorage.removeItem(OFF);
export { FIELDS as CLOUD_FIELDS };
